import React, { useState } from 'react';
import styled from 'styled-components';
import ExportModal from 'components/ExportModal';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';
import PropsTable from '../../components/PropsTable';

const PageTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const PageDescription = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 48px 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#F3F4F6' : '#1F2937')};
  margin: 0 0 24px 0;
`;

const DemoButton = styled.button`
  background: ${(props) => props.theme.colors.primary};
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e03e0c;
    transform: translateY(-1px);
  }
`;

const ExportModalDocs = () => {
  const [basicModalOpen, setBasicModalOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const props = [
    {
      name: 'isOpen',
      type: 'boolean',
      required: true,
      description: 'Controls modal visibility',
    },
    {
      name: 'onClose',
      type: 'function',
      required: true,
      description: 'Callback when modal should close',
    },
    {
      name: 'onExport',
      type: 'function',
      required: true,
      description:
        'Callback when export is triggered. Receives the format (CSV or PDF) as argument',
    },
    {
      name: 'isExporting',
      type: 'boolean',
      required: false,
      description:
        'Whether export is in progress. Disables interactions when true (default: false)',
    },
    {
      name: 'recordCount',
      type: 'number',
      required: false,
      description:
        'Number of records to export. Used for file size estimation (default: 0)',
    },
    {
      name: 'dataType',
      type: 'string',
      required: false,
      description:
        'Type of data being exported (e.g., "customers", "events", "credits"). Used in descriptions (default: "records")',
    },
  ];

  const handleExport = () => {
    setIsExporting(true);
    // Simulate export
    setTimeout(() => {
      setIsExporting(false);
      setBasicModalOpen(false);
      setCustomModalOpen(false);
    }, 2000);
  };

  return (
    <>
      <PageTitle>Export Modal</PageTitle>
      <PageDescription>
        A reusable modal component for exporting data in CSV or PDF formats.
        Includes format selection, file size estimation, and export progress
        states. Mobile-responsive with theme support.
      </PageDescription>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <PropsTable props={props} />
      </Section>

      <Section>
        <SectionTitle>Basic Export Modal</SectionTitle>
        <ComponentDemo
          title="Simple export modal with default settings"
          code={`import ExportModal from 'components/ExportModal';

const [showExportModal, setShowExportModal] = useState(false);
const [isExporting, setIsExporting] = useState(false);

const handleExport = async (format) => {
  setIsExporting(true);
  try {
    await startExport(format);
    setShowExportModal(false);
  } finally {
    setIsExporting(false);
  }
};

<ExportModal
  isOpen={showExportModal}
  onClose={() => setShowExportModal(false)}
  onExport={handleExport}
  isExporting={isExporting}
  recordCount={1500}
  dataType="customers"
/>`}
        >
          <DemoButton onClick={() => setBasicModalOpen(true)}>
            Open Export Modal
          </DemoButton>

          <ExportModal
            isOpen={basicModalOpen}
            onClose={() => setBasicModalOpen(false)}
            onExport={handleExport}
            isExporting={isExporting}
            recordCount={1500}
            dataType="customers"
          />
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Custom Data Type</SectionTitle>
        <ComponentDemo
          title="Export modal for events data"
          code={`<ExportModal
  isOpen={showExportModal}
  onClose={() => setShowExportModal(false)}
  onExport={handleExport}
  isExporting={isExporting}
  recordCount={245}
  dataType="events"
/>`}
        >
          <DemoButton onClick={() => setCustomModalOpen(true)}>
            Export Events
          </DemoButton>

          <ExportModal
            isOpen={customModalOpen}
            onClose={() => setCustomModalOpen(false)}
            onExport={handleExport}
            isExporting={isExporting}
            recordCount={245}
            dataType="events"
          />
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Import</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import ExportModal from 'components/ExportModal';`}
        />
      </Section>

      <Section>
        <SectionTitle>Usage with Export Hook</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`import { useState, useCallback } from 'react';
import ExportModal from 'components/ExportModal';
import useCustomerExport from './hooks/useCustomerExport';

const MyComponent = ({ accountId, recordCount }) => {
  const [showExportModal, setShowExportModal] = useState(false);
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useCustomerExport();

  const handleExport = useCallback(async (format) => {
    setShowExportModal(false);

    try {
      const jobId = await startExport(accountId, filters, format);

      subscribeToProgress(jobId, {
        onProgress: (status) => {
          // Update UI with progress
          console.log('Progress:', status.progress);
        },
        onComplete: async (data) => {
          const downloadUrl = await getDownloadUrl(jobId);
          window.open(downloadUrl, '_blank');
        },
        onError: (error) => {
          console.error('Export failed:', error);
        },
      });
    } catch (error) {
      console.error('Failed to start export:', error);
    }
  }, [accountId, startExport, subscribeToProgress, getDownloadUrl]);

  return (
    <>
      <button onClick={() => setShowExportModal(true)}>
        Export Report
      </button>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        recordCount={recordCount}
        dataType="customers"
      />
    </>
  );
};`}
        />
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        {/* eslint-disable jsx-a11y/accessible-emoji */}
        <ul style={{ lineHeight: 1.8 }}>
          <li>Format Selection - Choose between CSV and PDF export formats</li>
          <li>
            File Size Estimation - Displays estimated file size based on record
            count
          </li>
          <li>Theme Support - Fully theme-aware (light/dark mode)</li>
          <li>
            Mobile Responsive - Adapts to mobile screens with stacked layout
          </li>
          <li>Export Progress - Shows loading state during export</li>
          <li>
            Click Outside to Close - Closes when clicking overlay (disabled
            during export)
          </li>
          <li>
            Customizable Data Type - Descriptions update based on data type
          </li>
        </ul>
        {/* eslint-enable jsx-a11y/accessible-emoji */}
      </Section>

      <Section>
        <SectionTitle>File Size Calculation</SectionTitle>
        <CodeBlock
          language="javascript"
          code={`// The component uses exportUtils for size estimation
import { getFormattedExportSize } from 'utils/exportUtils';

// Bytes per record (approximate)
const BYTES_PER_RECORD = {
  CSV: 175,  // Typical CSV row
  PDF: 450,  // PDF with formatting
};

// Fixed overhead
const FILE_OVERHEAD = {
  CSV: 500,    // Headers
  PDF: 50000,  // PDF structure, fonts
};

// Example: 1000 customers
// CSV: 1000 * 175 + 500 = ~175 KB
// PDF: 1000 * 450 + 50000 = ~500 KB`}
        />
      </Section>
    </>
  );
};

export default ExportModalDocs;
